<template>
  <div class="form-section">
    <FormSkeleton v-if="loading" class="pb-8">
      <template #title>Document Upload</template>
    </FormSkeleton>
    
    <div v-else>
      <h4 class="text-responsive-lg font-bold text-center mb-responsive text-ietextlight">
        {{ title || 'Upload Documents' }}
      </h4>
      
      <div class="p-4 border rounded-md bg-white shadow-sm mb-6">
        <div class="space-y-responsive">
          <!-- Document Type Selection -->
          <FormField
            label="Document Type"
            field-id="document-type"
            :error="errors?.currentDocumentType"
            is-primary
            :required="areDocumentsRequiredForStep"
            help-text="Select the type of document you're uploading."
          >
            <select
              id="document-type"
              :value="currentDocumentType"
              @input="$emit('update:currentDocumentType', $event.target.value)"
              class="form-select w-full input-responsive"
            >
              <option value="" disabled>Select a document type</option>
              <option v-for="type in documentTypes" :key="type.value" :value="type.value">
                {{ type.label }}
              </option>
            </select>
          </FormField>
          
          <!-- Document Upload Section -->
          <div class="mt-6">
            <FormField
              label="Upload Document"
              field-id="document-upload"
              :error="errors?.documents"
              :required="areDocumentsRequiredForStep"
              help-text="Please upload the document in PDF, DOC, DOCX, JPG, JPEG, or PNG format."
            >
              <DocumentUpload
                v-model="internalDocumentFile"
                :id="documentUploadId"
                heading="Document Upload"
                :error="errors?.documents"
                @error="handleUploadError"
                @upload-start="uploadInProgress = true"
                @upload-complete="handleUploadComplete"
              />
              
              <!-- Upload Progress Indicator -->
              <div v-if="uploadInProgress && !internalDocumentFile" class="mt-3">
                <div class="w-full bg-gray-200 rounded-full h-2.5">
                  <div class="bg-blue-600 h-2.5 rounded-full animate-pulse" style="width: 70%"></div>
                </div>
                <p class="text-sm text-gray-600 mt-1">Processing your document...</p>
              </div>
            </FormField>
            
            <!-- Document Preview (for images) -->
            <div v-if="internalDocumentFile && isImageFile" class="mt-4 border rounded-md p-2 bg-gray-50">
              <img :src="documentPreviewUrl" alt="Document preview" class="max-h-40 mx-auto object-contain" />
            </div>
            
            <!-- Document Description Field -->
            <FormField
              v-if="internalDocumentFile"
              label="Document Description"
              field-id="document-description"
              :error="errors?.description"
              :show-optional-label="true"
              help-text="Add optional details about this document."
              highlight-new
            >
              <textarea
                id="document-description"
                :value="internalDocumentDescription"
                @input="$emit('update:documentDescription', $event.target.value)"
                rows="2"
                class="input-responsive resize-none"
                placeholder="Brief description of the uploaded document..."
              ></textarea>
            </FormField>
          </div>
          
          <!-- Device Capability Notice -->
          <div v-if="!cameraSupported && isMobile" class="mt-4 p-3 bg-yellow-50 rounded-md border border-yellow-200">
            <p class="text-sm text-yellow-700">
              <Icon icon="mdi:camera-off" class="inline mr-1" />
              Your device doesn't support direct camera access. You can still take a photo using your camera app and upload it.
            </p>
          </div>
          
          <!-- Multiple Documents Notice -->
          <div v-if="allowMultiple && internalDocumentFile" class="mt-4 p-4 bg-blue-50 rounded-md border border-blue-200">
            <p class="text-sm text-blue-700">
              <Icon icon="mdi:information-outline" class="inline mr-1" />
              Need to upload more documents? You'll be able to add additional documents after submitting this form.
            </p>
          </div>
        </div>
      </div>
      
      <!-- Navigation buttons -->
      <div class="flex-responsive-col justify-between mt-responsive">
        <button
          @click="$emit('previous')"
          class="btn-responsive-secondary mb-2 sm:mb-0 mobile-enhanced-button"
        >
          <Icon icon="mdi:arrow-left" class="mr-1" />
          Back
        </button>
        <button
          @click="$emit('next')"
          :class="customerType === 'lender' ? 'btn-lender' : 'btn-customer'"
          :disabled="!isValid || uploadInProgress"
          class="mobile-enhanced-button"
        >
          {{ nextButtonText }}
          <Icon icon="mdi:arrow-right" class="ml-1" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { Icon } from '@iconify/vue';
import { useMediaQuery } from '@vueuse/core';
import FormField from '@/components/common/FormField.vue';
import DocumentUpload from '@/components/common/DocumentUpload.vue';
import FormSkeleton from '@/components/common/FormSkeleton.vue';

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  documents: {
    type: Array,
    default: () => []
  },
  currentDocumentType: {
    type: String,
    default: ''
  },
  documentTypes: {
    type: Array,
    default: () => [
      { value: 'id_proof', label: 'Identification Proof' },
      { value: 'policy', label: 'Policy Document' },
      { value: 'certificate', label: 'Insurance Certificate' },
      { value: 'claim', label: 'Claim Document' },
      { value: 'billing', label: 'Billing Statement' },
      { value: 'other', label: 'Other Document' }
    ]
  },
  documentDescription: {
    type: String,
    default: ''
  },
  customerType: {
    type: String,
    default: 'customer'
  },
  errors: {
    type: Object,
    default: () => ({})
  },
  nextButtonText: {
    type: String,
    default: 'Next'
  },
  allowMultiple: {
    type: Boolean,
    default: false
  },
  initialLoading: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([
  'update:documents',
  'update:currentDocumentType',
  'update:documentDescription',
  'previous',
  'next',
  'upload-error'
]);

// Internal state
const internalDocumentFile = ref(null);
const internalDocumentDescription = ref('');
const documentPreviewUrl = ref('');
const uploadInProgress = ref(false);
const loading = ref(props.initialLoading);
const isMobile = useMediaQuery('(max-width: 768px)');
const cameraSupported = ref(false);

// Generate unique ID for document upload
const documentUploadId = `document-upload-${Math.random().toString(36).substring(2, 9)}`;

// Check device capabilities
onMounted(() => {
  // Short loading state for better UX
  setTimeout(() => {
    loading.value = false;
  }, 600);
  
  // Check if the browser supports the MediaDevices API
  cameraSupported.value = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
});

// Helper computed property to determine if documents are globally required for this step
const areDocumentsRequiredForStep = computed(() => {
  return props.customerType === 'lender';
});

// Watch for changes to document file and emit to parent
watch(internalDocumentFile, (newFile) => {
  console.log('[SharedDocUploadStep] Watcher for internalDocumentFile triggered.');
  console.log('[SharedDocUploadStep] props.documents before processing:', JSON.parse(JSON.stringify(props.documents)));
  console.log('[SharedDocUploadStep] newFile:', newFile ? { name: newFile.name, type: newFile.type, size: newFile.size } : null);
  console.log('[SharedDocUploadStep] props.currentDocumentType:', props.currentDocumentType);
  console.log('[SharedDocUploadStep] internalDocumentDescription before creating docEntry:', internalDocumentDescription.value);

  if (newFile && props.currentDocumentType) {
    const newDocuments = [...props.documents];
    const existingDocIndex = newDocuments.findIndex(doc => doc.type === props.currentDocumentType);

    const docEntry = {
      type: props.currentDocumentType,
      file: newFile,
      name: newFile.name,
      description: internalDocumentDescription.value
    };

    console.log('[SharedDocUploadStep] Created docEntry:', JSON.parse(JSON.stringify({ ...docEntry, file: 'File Object' })));

    if (existingDocIndex > -1) {
      newDocuments[existingDocIndex] = docEntry;
    } else {
      newDocuments.push(docEntry);
    }
    console.log('[SharedDocUploadStep] Emitting update:documents', JSON.parse(JSON.stringify(newDocuments)));
    emit('update:documents', newDocuments);
  }
  
  // Create preview URL for image files
  if (newFile && isImageFile.value) {
    documentPreviewUrl.value = URL.createObjectURL(newFile);
  } else {
    if (documentPreviewUrl.value) {
      URL.revokeObjectURL(documentPreviewUrl.value);
      documentPreviewUrl.value = '';
    }
  }
});

// Watch for changes to the document description from parent and update internal state
watch(() => props.documentDescription, (newDescription) => {
  console.log('[SharedDocUploadStep] props.documentDescription changed:', newDescription);
  internalDocumentDescription.value = newDescription;
});

// Check if the file is an image for preview
const isImageFile = computed(() => {
  if (!internalDocumentFile.value) return false;
  const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/jpg'];
  return imageTypes.includes(internalDocumentFile.value.type);
});

// Computed validation status
const isValid = computed(() => {
  if (uploadInProgress.value) {
    return false; // Always invalid if an upload is happening
  }
  if (props.customerType === 'customer') {
    // For customers, this step is always valid if no upload is in progress
    return true; 
  }
  // For lenders (or other types), require document type and file
  return !!(props.currentDocumentType && internalDocumentFile.value);
});

// Handle upload errors
const handleUploadError = (message) => {
  console.error('[SharedDocUploadStep] handleUploadError called with message:', message);
  uploadInProgress.value = false;
  emit('upload-error', message);
};

// Handle upload completion
const handleUploadComplete = () => {
  console.log('[SharedDocUploadStep] handleUploadComplete called. uploadInProgress set to false.');
  console.log('[SharedDocUploadStep] internalDocumentFile after upload complete:', internalDocumentFile.value ? { name: internalDocumentFile.value.name, type: internalDocumentFile.value.type } : null);
  uploadInProgress.value = false;
};

// Update the validation method to include null checks
const validateDocuments = () => {
  if (!props.documentTypes || !Array.isArray(props.documentTypes)) {
    return true; // If no document types defined, assume validation passes
  }
  
  const requiredDocTypes = props.documentTypes.filter(type => type.required);
  
  if (!requiredDocTypes.length) {
    return true; // No required documents
  }
  
  const requiredDocIds = requiredDocTypes.map(type => type.id);
  
  if (!Array.isArray(props.documents)) {
    return false; // No documents uploaded
  }
  
  return requiredDocIds.every(docId => 
    props.documents.some(doc => doc && doc.type === docId)
  );
};

// Update methods that work with the documents array
const handleFileUpload = (event, docType) => {
  const files = event.target.files;
  if (!files || !files.length) return;
  
  if (!Array.isArray(props.documents)) {
    props.documents = [];
  }
  
  // Rest of the method remains the same
};

// Update the computed property for uploaded document types
const uploadedDocumentTypes = computed(() => {
  if (!Array.isArray(props.documents)) {
    return [];
  }
  return props.documents.map(doc => doc.type);
});
</script>

<style scoped>
.form-section {
  @apply p-4 md:p-6;
}

.flex-responsive-col {
  @apply flex flex-col sm:flex-row sm:justify-between;
}

.btn-responsive-secondary {
  @apply px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition-colors flex items-center justify-center;
}

.input-responsive {
  @apply w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500;
  @apply py-3 px-4 text-base sm:py-2 sm:px-3 sm:text-sm;
}

.space-y-responsive {
  @apply space-y-4 md:space-y-6;
}

.mb-responsive {
  @apply mb-4 md:mb-6;
}

.mt-responsive {
  @apply mt-4 md:mt-6;
}

.text-responsive-lg {
  @apply text-lg md:text-xl;
}

.mobile-enhanced-button {
  @apply py-3 px-4 text-base sm:py-2 sm:px-3 sm:text-sm flex items-center justify-center;
}

/* Animation for loading states */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
.animate-pulse {
  animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style> 